import orderBy from 'lodash/orderBy'
import { RequestsFilterState } from 'src/apps/veterinary/forms/FormRequestsFilters'
import { RequestsFilters } from 'src/models/filters/RequestsFilters'
import { FilterState } from '../apps/veterinary/forms/FormDiaryFilters'
import { Facility, Family, Pet, User } from '../models'
import { AllEventMask, ChildEventMask, Conversation } from '../models/events'
import { EventStatus, EventType } from '../models/events/enums'
import { EventAdapter } from '../services/EventAdapter'
import { arrayToDictionary, safeArray, unique } from '../utils'
import { FacilitiesAPI } from './FacilitiesAPI'
import { PetsAPI } from './PetsAPI'
import { UsersAPI } from './UsersAPI'
import { axiosInstance } from './axios'

export type SearchOptionsParams = {
  page?: number
  sort?: string
  perPage?: number
  group_by?: string
  group_by_limit?: number
}

export const MAX_PAGE_SIZE = 100

export const DefaultSearchOptions: SearchOptionsParams = {
  page: 0,
  sort: '-payload.date',
  perPage: MAX_PAGE_SIZE,
}

const buildPayload = (facilityId: string, filters?: RequestsFilterState) => {
  interface Payload {
    [key: string]: any
  }

  const payload: Payload = {
    facilityId,
    status: {
      type: 'OR',
      values: [EventStatus.STATUS_NEW, EventStatus.STATUS_OPEN],
    },
  }

  if (filters) {
    if (filters.veterinarians?.length > 0) {
      payload.assigneeId = filters.veterinarians.includes('ASSIGNED')
        ? {
            type: 'NOT',
            values: null,
          }
        : {
            type: 'OR',
            values: filters.veterinarians,
          }
    }

    payload.status =
      filters.statuses?.length > 0
        ? {
            type: 'OR',
            values: filters.statuses,
          }
        : {
            type: 'OR',
            values: [EventStatus.STATUS_NEW, EventStatus.STATUS_OPEN, EventStatus.STATUS_CLOSED],
          }
  }

  return payload
}

const buildMobilePayload = (filters?: RequestsFilters) => {
  const payload: {
    [key: string]: any
  } = {}

  if (!!filters) {
    if (filters.customer) {
      payload.familyId = {
        type: 'OR',
        values: [filters.customer],
      }
    }
    if (filters.facilities.length > 0) {
      payload.facilityId = {
        type: 'OR',
        values: filters.facilities,
      }
    }

    payload.status =
      filters.statuses.length > 0
        ? {
            type: 'OR',
            values: filters.statuses,
          }
        : {
            type: 'OR',
            values: [EventStatus.STATUS_NEW, EventStatus.STATUS_OPEN, EventStatus.STATUS_CLOSED],
          }
  }

  return payload
}

export const getVeterinaryRequestQuery = (facility: Facility, filters?: RequestsFilterState) => ({
  parent_id: null,
  type: {
    type: 'OR',
    values: [EventType.EVENT_TYPE_HEALTH],
  },
  ...(filters && filters.period
    ? {
        period: filters.period,
      }
    : {}),
  payload: buildPayload(facility.id, filters),
})

export const getVeterinaryMobileRequestQuery = (filters?: RequestsFilters) => ({
  parent_id: null,
  type: {
    type: 'OR',
    values: [EventType.EVENT_TYPE_HEALTH],
  },
  payload: buildMobilePayload(filters),
})

export const getPetOwnerRequestQuery = (family: Family, filters: FilterState) => ({
  parent_id: {
    type: 'NOT',
    values: null,
  },
  type: {
    type: 'OR',
    values: [EventType.EVENT_TYPE_CHAT, EventType.EVENT_TYPE_BOT, EventType.EVENT_TYPE_CALENDAR],
  },
  payload: {
    familyId: family.id,
    ...(filters.statuses.length
      ? {
          status: {
            type: 'OR',
            values: filters.statuses,
          },
        }
      : {}),
    ...(filters.pets.length
      ? {
          pets: {
            type: 'OR',
            values: filters.pets.map((id) => [id]),
          },
        }
      : {}),
    ...(filters.payments.length
      ? {
          payment: {
            status: {
              type: 'OR',
              values: filters.payments,
            },
          },
        }
      : {}),
  },
})

export const getConversationMessagesQuery = (eventId: string, additional: any = {}) => ({
  parent_id: eventId,
  ...additional,
})

export const getSingleEvent = (eventId: string) => ({
  parent_id: eventId,
})

export const parseEventsToConversations = (events: AllEventMask[]) =>
  events.map((event: any) => {
    const evt = EventAdapter.from(event) as AllEventMask

    evt.addEnrichedData({
      Family: event.payload.family ? new Family({ ...event.payload.family, UserFamily: event.payload.customer }) : null,
      User: event.payload.user ? new User(event.payload.user) : null,
      Facility: event.payload.facility ? new Facility(event.payload.facility) : null,
      Pets: event.payload.pets.map((pet: any) => new Pet(pet)),
      Assignee: event.payload.assignee ? new User(event.payload.assignee) : null,
      Appointment: event.appointment ? event.appointment : null,
    })

    if (evt.Family || evt.User) {
      evt.payload.userId = evt.User?.id || ''
      evt.payload.familyId = evt.Family?.id || ''
      evt.payload.customerId = event.payload.customer?.id || ''
    }

    if (event.chat?.length > 0) {
      const lastMessageIndex = event.chat.length - 1

      if (lastMessageIndex >= 0) {
        const lastMessage = event.chat[lastMessageIndex]
        const lastMessageEvent = EventAdapter.from(lastMessage) as AllEventMask

        evt.addEnrichedData({ LastMessage: lastMessageEvent, LastUserMessage: lastMessageEvent.payload.user })
      }
    }

    let children = [] as AllEventMask[]

    children = children.concat(event.chat || ([] as AllEventMask[]))
    children = children.concat(event.appointment || ([] as AllEventMask[]))
    // children = children.concat(event.Facility)

    children = children.map((childEvent: any) => {
      const child = EventAdapter.from(childEvent) || ([] as ChildEventMask[])

      child.addEnrichedData({
        Family: childEvent.payload.family ? new Family(childEvent.payload.family) : null,
        User: childEvent.payload.user ? new User(childEvent.payload.user) : null,
        Facility: childEvent.payload.facility ? new Facility(childEvent.payload.facility) : null,
        Pets: childEvent.payload.pets.map((pet: any) => new Pet(pet)),
        Assignee: childEvent.payload.assignee ? new User(childEvent.payload.assignee) : null,
      })

      return child
    })

    return new Conversation(evt, children)
  })
